/**
 * O aviso do que mudou na plataforma.
 *
 * Abre sozinho quando há algo que esta pessoa ainda não viu, e some quando ela
 * fecha. O conteúdo é `novidades.js`; aqui mora só a decisão de mostrar e o
 * desenho.
 *
 * ## Quem entra pela primeira vez NÃO vê
 *
 * Para quem está chegando, tudo é novo — "novidades" só significa alguma coisa
 * para quem conhecia o anterior. E ela já recebe o convite do tour no primeiro
 * acesso: duas janelas empilhadas, e a pessoa fecha as duas sem ler nenhuma.
 *
 * Nesse caso o ponteiro é gravado na versão atual **sem mostrar nada**. Na
 * entrega seguinte ela é avisada como todo mundo.
 *
 * ## Uma vez por carregamento, e não por tela
 *
 * `AppShell` monta de novo a cada navegação. Sem a trava de módulo, trocar de
 * Agentes para Sistemas refaria a pergunta — e, no intervalo entre fechar e o
 * servidor registrar, a janela reabriria na cara de quem acabou de fechá-la.
 *
 * A trava vive fora do componente de propósito: ela é por carregamento da
 * página, não por instância.
 *
 * ## Falhar aqui é não mostrar nada
 *
 * Nenhum erro vai para a tela. Um aviso de novidades que derruba o portfólio
 * seria a pior troca possível — mesma regra do convite do tour.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../api.js';
import { VERSAO_ATUAL, novidadesPara, porEntrega } from '../novidades.js';

/** Já perguntei nesta carga da página? Ver o cabeçalho. */
let jaPerguntei = false;

/** Para o item de menu conseguir reabrir sem recarregar a página. */
let reabrir = null;

/** Abre o aviso com tudo o que existe, ignorando o ponteiro. */
export function abrirNovidades() {
  reabrir?.();
}

const DATA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long' });

/** `2026-10-26` → `26 de outubro`. Sem fuso: a data é um rótulo, não um instante. */
function comoSeLe(iso) {
  const [ano, mes, dia] = String(iso).split('-').map(Number);
  if (!ano || !mes || !dia) return iso;
  return `${DATA.format(new Date(ano, mes - 1, dia))} de ${ano}`;
}

export function Novidades({ admin }) {
  const [itens, setItens] = useState(null);

  const fechar = useCallback(() => {
    setItens(null);
    // Grava e esquece: se falhar, o aviso volta na próxima entrada, que é
    // melhor do que segurar a janela aberta por causa da rede.
    apiFetch('/api/me/novidades', {
      method: 'POST',
      body: JSON.stringify({ versao: VERSAO_ATUAL }),
    }).catch((err) => console.warn('nao registrei a leitura das novidades', err));
  }, []);

  // A porta de reabrir, para o menu do usuário.
  useEffect(() => {
    reabrir = () => setItens(novidadesPara({ versaoVista: null, admin }));
    return () => {
      reabrir = null;
    };
  }, [admin]);

  useEffect(() => {
    if (jaPerguntei) return undefined;
    jaPerguntei = true;

    let vivo = true;
    (async () => {
      try {
        const r = await apiFetch('/api/me/novidades');
        if (!vivo) return;

        // Primeira visita: alinha o ponteiro em silêncio. Ver o cabeçalho.
        if (r.primeiraVisita) {
          await apiFetch('/api/me/novidades', {
            method: 'POST',
            body: JSON.stringify({ versao: VERSAO_ATUAL }),
          });
          return;
        }

        const pendentes = novidadesPara({ versaoVista: r.versaoVista ?? null, admin });
        if (vivo && pendentes.length > 0) setItens(pendentes);
      } catch {
        /* sem aviso, e sem barulho */
      }
    })();

    return () => {
      vivo = false;
    };
  }, [admin]);

  useEffect(() => {
    if (!itens) return undefined;
    const aoTeclar = (e) => e.key === 'Escape' && fechar();
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [itens, fechar]);

  if (!itens || itens.length === 0) return null;

  const entregas = porEntrega(itens);
  const quantos = itens.length;

  return (
    <div className="modal-backdrop" role="presentation" onClick={fechar}>
      <div
        className="novidades-painel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="novidades-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="novidades-topo">
          <div>
            <span className="novidades-selo">
              {quantos === 1 ? '1 novidade' : `${quantos} novidades`}
            </span>
            <h2 id="novidades-titulo">O que há de novo no AI Hub</h2>
          </div>
          <button className="close-button" type="button" onClick={fechar} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className="novidades-corpo">
          {entregas.map((entrega) => (
            <section key={entrega.versao} className="novidades-entrega">
              <h3>{comoSeLe(entrega.data)}</h3>
              <ul>
                {entrega.itens.map((n) => (
                  <li key={n.id}>
                    <div className="novidades-item-titulo">
                      {n.titulo}
                      {/* O selo só é visto por quem vê o item — não-admin nem
                          recebe estes na lista. Serve para o administrador
                          saber que aquilo não apareceu para a equipe dele. */}
                      {n.publico === 'admin' && (
                        <span className="novidades-so-admin">administração</span>
                      )}
                    </div>
                    <p>{n.texto}</p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <footer className="novidades-pe">
          <span>Você pode reabrir isto no menu do seu nome.</span>
          <button type="button" className="button primary" onClick={fechar}>
            Entendi
          </button>
        </footer>
      </div>
    </div>
  );
}

export default Novidades;
